import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type {
  AgendaKind,
  ChangeAgendaEmailTemplate,
  ChangeAgendaQueuedSendSummaryItem,
  ChangeAgendaRecipient,
  ChangeAgendaSendKind,
  ChangeAgendaSendSummaryItem,
} from '../../../types/changes';
import { defaultLayoutForAgendaKind } from '../../../types/changes';
import { describeAgendaEmailDispatch } from '../agendaEmailDispatchStatus';
import { getApiErrorMessage } from '../../../utils/apiError';
import AgendaQueuedDispatchList from './AgendaQueuedDispatchList';

const SEND_GROUPS: Array<{ kind: ChangeAgendaSendKind; label: string }> = [
  { kind: 'committee', label: 'Riscos e Impactos' },
  { kind: 'camud', label: 'CAMUD Go/No-Go' },
];

function formatSentAt(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function layoutLabelForKind(layouts: ChangeAgendaEmailTemplate[], kind: AgendaKind): string {
  const key = defaultLayoutForAgendaKind(kind);
  const named = layouts.find((item) => item.key === key);
  const global = layouts.find((item) => item.scope === 'global' || item.key === 'global');
  const globalLabel = global?.label || 'Padrão (todos os comitês)';
  if (named?.enabled) return named.label || key;
  if (named && !named.enabled) return `${globalLabel} (padrão global — layout do grupo desligado)`;
  return `${globalLabel} (padrão global)`;
}

interface Props {
  changeId?: number;
  changeIds?: number[];
  changeTitle?: string;
  windowStart: string;
  windowEnd: string;
  categoryId?: number;
  project?: string;
  ownerId?: number;
  initialKinds?: ChangeAgendaSendKind[];
  sentByKind: Partial<Record<ChangeAgendaSendKind, ChangeAgendaSendSummaryItem>>;
  sentItems?: ChangeAgendaSendSummaryItem[];
  queuedItems?: ChangeAgendaQueuedSendSummaryItem[];
  onSent: (message: string) => void;
  onSentRefresh: () => void;
  /** Fecha a modal após envio imediato concluído (agendamento mantém aberta). */
  onComplete?: () => void;
  embedded?: boolean;
}

const CalendarChangeAgendaPanel: React.FC<Props> = ({
  changeId,
  changeIds,
  changeTitle,
  windowStart,
  windowEnd,
  categoryId,
  project,
  ownerId,
  initialKinds,
  sentByKind,
  sentItems = [],
  queuedItems = [],
  onSent,
  onSentRefresh,
  onComplete,
  embedded = true,
}) => {
  const resolvedIds = (changeIds && changeIds.length ? changeIds : changeId ? [changeId] : []).filter(
    (id) => Number.isFinite(id) && id > 0,
  );
  const previewChangeId = resolvedIds[0];
  const { showConfirm } = useModal();
  const [selected, setSelected] = useState<ChangeAgendaSendKind[]>(
    initialKinds?.length ? initialKinds : ['committee'],
  );
  const [recipientsByKind, setRecipientsByKind] = useState<
    Partial<Record<ChangeAgendaSendKind, ChangeAgendaRecipient[]>>
  >({});
  const [layouts, setLayouts] = useState<ChangeAgendaEmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleMode, setScheduleMode] = useState<'now' | 'custom'>('now');
  const [customSendAt, setCustomSendAt] = useState('');

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const kinds: ChangeAgendaSendKind[] = ['committee', 'camud'];
      const [layoutResponse, ...recipientResponses] = await Promise.all([
        changesApi.listAgendaEmailLayouts().catch(() => ({ items: [] })),
        ...kinds.map((kind) =>
          changesApi.listAgendaRecipients({
            agenda_kind: kind,
            change_id: previewChangeId,
            limit: 50,
          }),
        ),
      ]);
      setLayouts(layoutResponse.items || []);
      const next: Partial<Record<ChangeAgendaSendKind, ChangeAgendaRecipient[]>> = {};
      kinds.forEach((kind, index) => {
        next[kind] = recipientResponses[index]?.items || [];
      });
      setRecipientsByKind(next);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar destinatários da pauta.'));
    } finally {
      setLoading(false);
    }
  }, [previewChangeId]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const toggleKind = (kind: ChangeAgendaSendKind, checked: boolean) => {
    setSelected((prev) =>
      checked ? Array.from(new Set([...prev, kind])) : prev.filter((value) => value !== kind),
    );
  };

  const selectedPeopleCount = useMemo(() => {
    const emails = new Set<string>();
    for (const kind of selected) {
      for (const person of recipientsByKind[kind] || []) {
        if (person.email) emails.add(person.email.toLowerCase());
      }
    }
    return emails.size;
  }, [recipientsByKind, selected]);

  const dispatchStatus = describeAgendaEmailDispatch({ sentItems, queuedItems });

  const handleSend = async () => {
    if (!selected.length) {
      setError('Marque ao menos um grupo para enviar a pauta.');
      return;
    }
    if (!resolvedIds.length) {
      setError('Não há mudanças na pauta para enviar.');
      return;
    }
    const labels = SEND_GROUPS.filter((group) => selected.includes(group.kind)).map((group) => group.label);
    const targetLabel =
      resolvedIds.length === 1
        ? `a pauta da mudança #${resolvedIds[0]}${changeTitle ? ` (${changeTitle})` : ''}`
        : `a pauta desta reunião (${resolvedIds.length} mudanças)${changeTitle ? ` · ${changeTitle}` : ''}`;
    const scheduleLabel =
      scheduleMode === 'custom' && customSendAt
        ? `agendado para ${customSendAt}`
        : 'agora (fila assíncrona de e-mail)';
    const confirmed = await showConfirm({
      title: 'Enviar e-mail da pauta',
      message: `Deseja enviar ${targetLabel}?`,
      details: `${dispatchStatus.modalParagraph} Novo disparo: grupos ${labels.join(', ')} · ${selectedPeopleCount} destinatário(s) · ${scheduleLabel}.`,
      variant: 'primary',
      confirmLabel: 'Enviar e-mail',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    if (scheduleMode === 'custom' && !customSendAt.trim()) {
      setError('Informe a data e hora para agendar o envio.');
      return;
    }

    setSending(true);
    setError(null);
    try {
      const results = [];
      for (const kind of selected) {
        const result = await changesApi.sendAgenda({
          agenda_kind: kind,
          window_start: windowStart,
          window_end: windowEnd,
          category_id: categoryId,
          project,
          owner_id: ownerId,
          change_ids: resolvedIds,
          layout: defaultLayoutForAgendaKind(kind),
          ...(scheduleMode === 'custom' && customSendAt ? { send_at: customSendAt } : {}),
        });
        results.push({ kind, result });
        if (result.outcome === 'queued' || result.queued) {
          const kindLabel = SEND_GROUPS.find((g) => g.kind === kind)?.label || kind;
          onSent(`E-mail agendado (${kindLabel}): ${result.send_at || 'data informada'}.`);
          onSentRefresh();
          return;
        }
      }
      const sent = results.reduce((sum, item) => sum + (item.result.sent || 0), 0);
      const empty = results.filter((item) => item.result.changes === 0);
      if (empty.length === results.length) {
        setError('Não foi possível incluir esta mudança na pauta. Confira as datas de governança.');
        return;
      }
      if (sent === 0) {
        setError('Pauta montada, mas nenhum destinatário válido foi encontrado.');
        return;
      }
      onSent(
        resolvedIds.length === 1
          ? `Pauta enviada da mudança #${resolvedIds[0]}: ${labels.join(', ')} · ${sent} destinatário(s).`
          : `Pauta enviada da reunião (${resolvedIds.length} mudanças): ${labels.join(', ')} · ${sent} destinatário(s).`,
      );
      onSentRefresh();
      onComplete?.();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao enviar pauta.'));
    } finally {
      setSending(false);
    }
  };

  const wrapperClass = embedded ? 'border-top pt-2 mt-2' : '';

  return (
    <div className={wrapperClass}>
      <div className="small text-muted mb-2">
        {resolvedIds.length > 1
          ? 'Entram no e-mail as mudanças desta reunião. Cadastro de pessoas e layouts fica em Cadastros → Comitês.'
          : 'Só esta mudança entra no e-mail. Cadastro de pessoas e layouts fica em Cadastros → Comitês.'}
      </div>
      <Alert variant={dispatchStatus.hasHistory ? 'warning' : 'info'} className="py-2 small mb-2">
        {dispatchStatus.headline}
      </Alert>
      <AgendaQueuedDispatchList
        items={queuedItems}
        onChanged={onSentRefresh}
        onInfo={onSent}
      />
      {error ? (
        <Alert variant="danger" className="py-2" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <Form.Group className="mb-2">
        <Form.Label className="small fw-semibold">Quando disparar</Form.Label>
        <div className="d-flex flex-wrap gap-3">
          <Form.Check
            type="radio"
            id={`change-send-now-${previewChangeId || 'pauta'}`}
            name={`change-schedule-${previewChangeId || 'pauta'}`}
            label="Agora"
            checked={scheduleMode === 'now'}
            onChange={() => setScheduleMode('now')}
          />
          <Form.Check
            type="radio"
            id={`change-send-custom-${previewChangeId || 'pauta'}`}
            name={`change-schedule-${previewChangeId || 'pauta'}`}
            label="Agendar data/hora"
            checked={scheduleMode === 'custom'}
            onChange={() => setScheduleMode('custom')}
          />
        </div>
        {scheduleMode === 'custom' ? (
          <Form.Control
            type="datetime-local"
            className="mt-2"
            style={{ maxWidth: 260 }}
            value={customSendAt}
            onChange={(e) => setCustomSendAt(e.target.value)}
            aria-label="Data e hora do envio"
          />
        ) : null}
      </Form.Group>
      {loading ? <div className="text-muted small mb-2">Carregando grupos e destinatários…</div> : null}
      {SEND_GROUPS.map((group) => {
        const people = recipientsByKind[group.kind] || [];
        const sent = sentByKind[group.kind];
        return (
          <div key={group.kind} className="border rounded p-2 mb-2 bg-white">
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
              <Form.Check
                type="checkbox"
                id={`agenda-kind-${previewChangeId || 'pauta'}-${group.kind}`}
                label={group.label}
                checked={selected.includes(group.kind)}
                onChange={(event) => toggleKind(group.kind, event.target.checked)}
              />
              {sent?.last_sent_at ? (
                <Badge bg="success" text="light" className="fw-normal">
                  Já enviado em {formatSentAt(sent.last_sent_at)}
                </Badge>
              ) : null}
              {queuedItems
                .filter((row) => row.agenda_kind === group.kind || row.agenda_kind === 'both')
                .map((row) => (
                  <Badge key={`q-${row.queued_id}`} bg="warning" text="dark" className="fw-normal">
                    Agendado para {formatSentAt(row.send_at)}
                  </Badge>
                ))}
            </div>
            <div className="small text-muted mt-1">
              Layout: {layoutLabelForKind(layouts, group.kind)}
            </div>
            {people.length === 0 && !loading ? (
              <div className="small text-muted mt-1">Nenhuma pessoa ativa neste comitê.</div>
            ) : (
              <ul className="small mb-0 mt-1 ps-3">
                {people.map((person) => (
                  <li key={`${group.kind}-${person.person_id || person.email}`}>
                    {person.name} · {person.email}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      <Button
        size="sm"
        disabled={sending || loading || selected.length === 0}
        onClick={() => void handleSend()}
      >
        Enviar e-mail
      </Button>
    </div>
  );
};

export default CalendarChangeAgendaPanel;
